import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import TableComponent from '../../../../components/table/TableComponent';
import { useTranslation } from 'react-i18next';
import ProductModel from '../../../../components/model/ProductModel';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'react-alert';
import ProductForm from './ProductForm';
import { setDirection, setPage, setSortField } from '../../../../redux/slices/PaginationSlice';
import { createProduct, deleteProduct, getAllProductsGroups, getProducts, updateProduct } from './ProductService';
import { useOnMount } from '../../../../components/common/Hooks';
import { SearchFilter } from '../../../../components/filter/SearchFilter';
import { ConfirmationModal } from '../../../../components/modal/ConfirmationModal';
import { isMobile } from 'react-device-detect';
import { ToggleFilterButton } from '../../../../components/buttons/ToggleFilterButton';
import { EditButton } from '../../../../components/buttons/EditButton';
import { DeleteButton } from '../../../../components/buttons/DeleteButton';
import { CopyButton } from '../../../../components/buttons/CopyButton';

export const ProductsPage = () => {

    const { t, i18n } = useTranslation();
    const alert = useAlert();
    const onMount = useOnMount();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const pagination = useSelector((state) => state.pagination);
    const [products, setProducts] = useState([]);
    const [record, setRecord] = useState(ProductModel.initModel);
    const [productGroups, setProductGroups] = useState([{ name: t('common:SELECT'), value: -1 }]);
    const [filterValue, setFilterValue] = useState('');
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const handleCloseModal = () => setIsConfirmOpen(false);

    useEffect(() => {
        if (onMount) {
            init();
        } else {
            load();
        }

    }, [pagination.page, pagination.sortByField, pagination.sortingDirection]);

    const init = () => {
        getAllProductsGroups(i18n.language.toUpperCase())
            .then((response) => {
                setProductGroups(productGroups.concat(response.data.map(it => ({ name: it.title, value: it.id }))
                    .sort((a, b) => a.name > b.name ? 1 : -1)));
            })
            .catch(() => alert.error(t('alert:loadingError')));
        if (pagination.sortByField === 'title'
            && pagination.page === 0
            && pagination.sortingDirection === 'ASC') {
            load();
        } else {
            dispatch(setSortField('title'));
            dispatch(setPage(0));
            dispatch(setDirection('ASC'));
        }
    };

    const load = () => {
        let request = {
            page: pagination.page,
            size: pagination.size,
            ascDesc: pagination.sortingDirection,
            sortField: pagination.sortByField,
            lang: i18n.language.toUpperCase(),
            title: filterValue,
        };
        getProducts(request)
            .then((response) => setProducts(response.data))
            .catch(() => alert.error(t('alert:loadingError')))
            .finally(() => setLoading(false));
    };

    const openConfirmation = (product) => {
        setItemToDelete(product);
        setIsConfirmOpen(true);
    };

    const deleteRecord = () => {
        deleteProduct(itemToDelete.id)
            .then(() => {
                alert.success(t('alert:deleteSuccess'));
                load();
            })
            .catch(() => alert.error(t('alert:deleteError')));
        handleCloseModal();
    };

    const editRecord = (productObj) => {
        setRecord(productObj);
        document.getElementById('createButton').click();
    };

    const copyRecord = (productObj) => {
        setRecord({ ...productObj, id: null, title: productObj.title + t('common:copy') });
        document.getElementById('createButton').click();
    };


    const handleSubmit = (values, recordId) => {
        recordId !== null
            ? update(values, recordId)
            : insertRecord(values);
    };

    const handleCancel = () => {
        setRecord(ProductModel.initModel);
    };

    const insertRecord = (values) => {
        createProduct(values)
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                load();
            })
            .catch(() => alert.error(t('alert:saveError')));
    };

    const update = (values, recordId) => {
        updateProduct({ ...values, id: recordId })
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                load();
            })
            .catch(() => alert.error(t('alert:saveError')));
    };

    const getButtons = () => {
        return [
            { element: <EditButton onClick={editRecord} /> },
            { element: <DeleteButton onClick={openConfirmation} /> },
            { element: <CopyButton onClick={copyRecord} /> }
        ];
    };

    return (
        <>
            <div className={isMobile ? 'mobile-filter-container' : 'filter-container'}>
                <ProductForm onClick={handleSubmit}
                             onCancel={handleCancel}
                             record={record}
                             productGroups={productGroups} />
                {isMobile ? <ToggleFilterButton></ToggleFilterButton> : <></>}
                <SearchFilter doFilter={load} value={filterValue} setValue={setFilterValue}></SearchFilter>
            </div>
            <ConfirmationModal
                message={t('products:deleteConfirm') + (itemToDelete != null ? itemToDelete.title : '') + '?'}
                isShowing={isConfirmOpen}
                onConfirm={deleteRecord}
                onClose={handleCloseModal}>
            </ConfirmationModal>
            {loading ? (
                <Spinner animation='border' className='container' />
            ) : (
                <TableComponent
                    model={ProductModel}
                    translationsGroup='products:'
                    columnsNames={[
                        'title',
                        'proteins',
                        'carbohydrates',
                        'fat',
                        'energyValueKcal',
                    ]}
                    totalElements={products.totalElements}
                    contents={products.content}
                    buttons={getButtons()}
                />
            )}
        </>
    );
};
